// components/CreateAccountModal.jsx
import React, { useState, useEffect } from "react";
import { X, Building2, Loader } from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const CreateAccountModal = ({ onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState({
    name: "",
    type: "Prospect",
    industry: "",
    website: "",
    phone: "",
    billingStreet: "",
    billingCity: "",
    billingState: "",
    billingPostalCode: "",
    billingCountry: "",
    numberOfEmployees: "",
    annualRevenue: "",
    description: "",
    rating: "",
  });
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedOwnerId, setSelectedOwnerId] = useState("");

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await apiService.getUsers();
      setUsers(response.data);
      if (response.data.length > 0) {
        setSelectedOwnerId(response.data[0].id);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const nullIfEmpty = (val) => (val === "" ? undefined : val);

    try {
      await apiService.createAccount({
        name: formData.name,
        type: nullIfEmpty(formData.type),
        industry: nullIfEmpty(formData.industry),
        rating: nullIfEmpty(formData.rating),
        website: nullIfEmpty(formData.website),
        phone: nullIfEmpty(formData.phone),
        billingStreet: nullIfEmpty(formData.billingStreet),
        billingCity: nullIfEmpty(formData.billingCity),
        billingState: nullIfEmpty(formData.billingState),
        billingPostalCode: nullIfEmpty(formData.billingPostalCode),
        billingCountry: nullIfEmpty(formData.billingCountry),
        description: nullIfEmpty(formData.description),
        ownerId: selectedOwnerId || undefined,
        numberOfEmployees: formData.numberOfEmployees
          ? parseInt(formData.numberOfEmployees)
          : undefined,
        annualRevenue: formData.annualRevenue
          ? parseFloat(formData.annualRevenue)
          : undefined,
      });

      onSuccess();
    } catch (error) {
      console.error("Error creating account:", error);
      alert("Failed to create account. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Theme helpers
  const inputClass = `w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 ${
    isDarkMode
      ? "bg-[#13161B] border-gray-700 text-white placeholder-gray-500"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDarkMode ? "text-gray-300" : "text-gray-700"
  }`;

  const sectionHeadingClass = `text-sm font-medium mb-4 ${
    isDarkMode ? "text-gray-300" : "text-gray-700"
  }`;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div
        className={`rounded-xl border w-full max-w-3xl max-h-[90vh] overflow-y-auto ${
          isDarkMode
            ? "bg-[#0C0E12] border-gray-700"
            : "bg-white border-gray-200"
        }`}
      >
        {/* Header */}
        <div
          className={`sticky top-0 border-b p-6 flex items-center justify-between z-10 ${
            isDarkMode
              ? "bg-[#0C0E12] border-gray-700"
              : "bg-white border-gray-200"
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-600/10 flex items-center justify-center">
              <Building2 className="text-blue-400" size={20} />
            </div>
            <div>
              <h2
                className={`text-xl font-semibold ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Create Account
              </h2>
              <p
                className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Add a new business account
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`transition-colors ${
              isDarkMode
                ? "text-gray-400 hover:text-white"
                : "text-gray-500 hover:text-gray-900"
            }`}
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Account Information */}
          <div>
            <h3 className={sectionHeadingClass}>Account Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className={labelClass}>
                  Account Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className={inputClass}
                  placeholder="Enter account name"
                />
              </div>

              <div>
                <label className={labelClass}>Account Type</label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  className={inputClass}
                >
                  <option value="Analyst">Analyst</option>
                  <option value="Competitor">Competitor</option>
                  <option value="Customer">Customer</option>
                  <option value="Customer CCTV">Customer CCTV</option>
                  <option value="Customer Printer">Customer Printer</option>
                  <option value="Customer Network Only">
                    Customer Network Only
                  </option>
                  <option value="Grade A Team installation">
                    Grade A Team installation
                  </option>
                  <option value="Integrator">Integrator</option>
                  <option value="Investor">Investor</option>
                  <option value="Partner">Partner</option>
                  <option value="Prospect">Prospect</option>
                  <option value="Reseller">Reseller</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className={labelClass}>Industry</label>
                <select
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                  className={inputClass}
                >
                  <option value="">Select industry...</option>
                  <option value="Agriculture">Agriculture</option>
                  <option value="Apparel">Apparel</option>
                  <option value="Banking">Banking</option>
                  <option value="Biotechnology">Biotechnology</option>
                  <option value="Chemicals">Chemicals</option>
                  <option value="Communications">Communications</option>
                  <option value="Construction">Construction</option>
                  <option value="Consulting">Consulting</option>
                  <option value="Education">Education</option>
                  <option value="Electronics">Electronics</option>
                  <option value="Energy">Energy</option>
                  <option value="Engineering">Engineering</option>
                  <option value="Entertainment">Entertainment</option>
                  <option value="Environmental">Environmental</option>
                  <option value="Finance">Finance</option>
                  <option value="Food & Beverage">Food & Beverage</option>
                  <option value="Government">Government</option>
                  <option value="Healthcare">Healthcare</option>
                  <option value="Hospitality">Hospitality</option>
                  <option value="Insurance">Insurance</option>
                  <option value="Machinery">Machinery</option>
                  <option value="Manufacturing">Manufacturing</option>
                  <option value="Media">Media</option>
                  <option value="Not For Profit">Not For Profit</option>
                  <option value="Recreation">Recreation</option>
                  <option value="Retail">Retail</option>
                  <option value="Shipping">Shipping</option>
                  <option value="Technology">Technology</option>
                  <option value="Telecommunications">Telecommunications</option>
                  <option value="Transportation">Transportation</option>
                  <option value="Utilities">Utilities</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className={labelClass}>Rating</label>
                <select
                  name="rating"
                  value={formData.rating}
                  onChange={handleChange}
                  className={inputClass}
                >
                  <option value="">Select rating...</option>
                  <option value="Hot">Hot</option>
                  <option value="Warm">Warm</option>
                  <option value="Cold">Cold</option>
                </select>
              </div>

              <div>
                <label className={labelClass}>Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              <div>
                <label className={labelClass}>Website</label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="https://example.com"
                />
              </div>

              <div>
                <label className={labelClass}>Number of Employees</label>
                <input
                  type="number"
                  name="numberOfEmployees"
                  value={formData.numberOfEmployees}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="0"
                />
              </div>

              <div>
                <label className={labelClass}>Annual Revenue (£)</label>
                <input
                  type="number"
                  name="annualRevenue"
                  value={formData.annualRevenue}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="0"
                />
              </div>

              <div>
                <label className={labelClass}>Account Owner</label>
                <select
                  value={selectedOwnerId}
                  onChange={(e) => setSelectedOwnerId(e.target.value)}
                  className={inputClass}
                >
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Billing Address */}
          <div>
            <h3 className={sectionHeadingClass}>Billing Address</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className={labelClass}>Street</label>
                <input
                  type="text"
                  name="billingStreet"
                  value={formData.billingStreet}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="123 Main Street"
                />
              </div>

              <div>
                <label className={labelClass}>City</label>
                <input
                  type="text"
                  name="billingCity"
                  value={formData.billingCity}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="London"
                />
              </div>

              <div>
                <label className={labelClass}>State/County</label>
                <input
                  type="text"
                  name="billingState"
                  value={formData.billingState}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="Greater London"
                />
              </div>

              <div>
                <label className={labelClass}>Postal Code</label>
                <input
                  type="text"
                  name="billingPostalCode"
                  value={formData.billingPostalCode}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="SW1A 1AA"
                />
              </div>

              <div>
                <label className={labelClass}>Country</label>
                <input
                  type="text"
                  name="billingCountry"
                  value={formData.billingCountry}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="United Kingdom"
                />
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className={labelClass}>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
              className={inputClass}
              placeholder="Enter account description..."
            />
          </div>

          {/* Actions */}
          <div
            className={`flex gap-3 pt-4 border-t ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <button
              type="button"
              onClick={onClose}
              className={`flex-1 px-4 py-2.5 border rounded-lg font-medium transition-colors ${
                isDarkMode
                  ? "bg-[#13161B] border-gray-700 text-gray-300 hover:bg-gray-700"
                  : "bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2.5 text-white rounded-lg font-medium transition-all duration-300 relative hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{
                background:
                  "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
                border: "1px solid",
                borderImageSource:
                  "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
                boxShadow: `
                  0px 36px 32px -16px #427BCA36,
                  0px 8px 16px 0px #2F578F1F,
                  0px 2px 4px -8px #427BCA14,
                  0px 8px 24px -16px #FFFFFF3D inset,
                  0px -24px 32px 0px #FFFFFF38 inset,
                  0px 0px 12px 0px #FFFFFF inset
                `,
              }}
            >
              {loading ? (
                <>
                  <Loader className="animate-spin" size={16} />
                  Creating...
                </>
              ) : (
                "Create Account"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateAccountModal;
